import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";
import DataLoader from "../components/globalView/DataLoader";
import {
  getWhitelistedIps,
  addWhitelistedIp,
  deleteWhitelistedIp,
  handleLoadState
} from "../store/actions";

const isValidIP = (ip) => {
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?\d{1,2})\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?\d{1,2})$/;
  return ipRegex.test(ip);
};

const WhitelistedIps = () => {
  const dispatch = useDispatch();
  const [newIp, setNewIp] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isAddingIp, setIsAddingIp] = useState(false);
  const [localIps, setLocalIps] = useState([]);

  const isLoading = useSelector((state) => state.userReducer.loading);
  const whitelistedIps = useSelector((state) => state.userReducer.whitelistedIps || []);

  useEffect(() => {
    dispatch(handleLoadState(true));
    dispatch(getWhitelistedIps()).finally(() => dispatch(handleLoadState(false)));
  }, [dispatch]);

  useEffect(() => {
    setLocalIps(whitelistedIps);
  }, [whitelistedIps]);

  const addIp = () => {
    if (isValidIP(newIp)) {
      if (localIps.includes(newIp)) {
        setError('This IP is already in the list.');
      } else {
        setError('');
        setIsAddingIp(true);
        dispatch(addWhitelistedIp(newIp))
          .then(() => {
            setNewIp('');
            setSuccessMessage('IP added successfully');
            setTimeout(() => setSuccessMessage(''), 2000);
          })
          .catch(() => {
            setError('Failed to add IP. Please try again.');
          })
          .finally(() => {
            setIsAddingIp(false);
          });
      }
    } else {
      setError('Please enter a valid IP address.');
    }
  };

  const deleteIp = (ipToDelete) => {
    setLocalIps(prevIps => prevIps.filter(ip => ip !== ipToDelete));
    dispatch(deleteWhitelistedIp(ipToDelete))
      .then(() => {
        setSuccessMessage('IP deleted successfully');
        setTimeout(() => setSuccessMessage(''), 2000);
      })
      .catch(() => {
        setLocalIps(prevIps => [...prevIps, ipToDelete]);
        setError('Failed to delete IP. Please try again.');
        setTimeout(() => setError(''), 2000);
      });
  };

  if (isLoading) {
    return <DataLoader />;
  }

  return (
    <>
      <Helmet>
        <title>IP Whitelist Management | Instaswap Partners</title>
      </Helmet>
      <div>
        <div className="secHdr">
          <h2 className="secTitle">Whitelisted IPs</h2>
          <p className="secLead">
            IP whitelisting for Wallet Sign API integration only. Requests to
            signing endpoints are accepted exclusively from the addresses listed
            here.
          </p>
        </div>

        <div className="wlAddBar">
          <input
            className="input"
            type="text"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addIp();
            }}
            placeholder="Add an IP address, e.g. 203.0.113.24"
          />
          <button
            type="button"
            className="btnPrimary"
            style={{ height: 48 }}
            onClick={addIp}
            disabled={isAddingIp}
          >
            {isAddingIp ? <FontAwesomeIcon icon={faSpinner} spin /> : "Add new IP"}
          </button>
        </div>

        {error && (
          <div className="notice red" style={{ marginBottom: 14, maxWidth: 680 }}>
            <span className="ni">⚠</span>
            <div>{error}</div>
          </div>
        )}
        {successMessage && (
          <div className="notice green" style={{ marginBottom: 14, maxWidth: 680 }}>
            <span className="ni">✓</span>
            <div>{successMessage}</div>
          </div>
        )}

        <div className="gtScroll" style={{ maxWidth: 860 }}>
          <div
            className="gtHead"
            style={{ gridTemplateColumns: "1.4fr 130px 80px" }}
          >
            <span>IP address</span>
            <span>Status</span>
            <span className="r"></span>
          </div>
          {localIps.length > 0 ? (
            localIps.map((ip) => (
              <div
                key={ip}
                className="gtRow"
                style={{ gridTemplateColumns: "1.4fr 130px 80px", minHeight: 58 }}
              >
                <div className="cellMono" style={{ fontSize: 13.5, color: "#eef3f0" }}>
                  {ip}
                </div>
                <div>
                  <span className="pill done">
                    <span className="pd" />
                    Active
                  </span>
                </div>
                <div className="r">
                  <button
                    type="button"
                    className="iconBtn danger"
                    onClick={() => deleteIp(ip)}
                    aria-label={`Delete ${ip}`}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="gtEmpty">No IPs whitelisted yet.</div>
          )}
        </div>

        <p className="wlHint">
          Tip: whitelist your backend's static egress IPs. Only requests from
          whitelisted IPs will be accepted.
        </p>
      </div>
    </>
  );
};

export default WhitelistedIps;
